import React, { useEffect, useState } from 'react';
import { Row, Col, Spinner } from 'reactstrap';
import { api } from '../../../../../../../../config';
import { toast } from 'react-toastify';
import { unwrapApiItems } from '../../../../../../../../shared/api/unwrap';
import {
  mapSupportDocumentItem,
  mapCounterDocumentItem,
  isOtherSupportDocument,
  mergeDocumentsWithCounters,
  DocumentRow,
} from '../../../../../../../../shared/offer-domain/document';

const SupportingDocumentTab = ({ procurementId, token }) => {
  const [procurementDocuments, setProcurementDocuments] = useState([]);
  const [otherDocuments, setOtherDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!procurementId || !token) return;

    let cancelled = false;

    const fetchDocuments = async () => {
      setIsLoading(true);
      try {
        const [docResponse, counterResponse] = await Promise.all([
          fetch(
            `${api.API_URL}/procurement/get-procurement-document?ProcurementId=${encodeURIComponent(
              procurementId
            )}`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }
          ),
          fetch(
            `${api.API_URL}/procurement-document-counter/by-procurement/${encodeURIComponent(
              procurementId
            )}`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        ]);

        if (!docResponse.ok) {
          throw new Error(`HTTP error! status: ${docResponse.status}`);
        }

        const docResult = await docResponse.json();
        let counters = [];
        if (counterResponse.ok) {
          const counterResult = await counterResponse.json();
          counters = unwrapApiItems(counterResult).map(mapCounterDocumentItem);
        }

        const items = unwrapApiItems(docResult)
          .map(mapSupportDocumentItem)
          .filter(item => item.isShowToDMUT === true || item.isShowToDMUT === 1)
          .map(item => ({
            ...item,
            updated: item.lastUpdated || item.createdDate,
          }));

        const merged = mergeDocumentsWithCounters(items, counters);

        if (!cancelled) {
          setProcurementDocuments(
            merged.filter(item => !isOtherSupportDocument(item))
          );
          setOtherDocuments(
            merged.filter(item => isOtherSupportDocument(item))
          );
        }
      } catch (error) {
        if (!cancelled) {
          setProcurementDocuments([]);
          setOtherDocuments([]);
          toast.error('Failed to load supporting documents');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchDocuments();
    return () => {
      cancelled = true;
    };
  }, [procurementId, token]);

  const handleView = url => {
    if (!url) {
      toast.error('File is not available');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getBadgeMeta = doc => {
    if (doc.hasCounter) {
      return {
        label: 'Countered',
        className: 'bg-warning-subtle text-warning',
      };
    }
    if (doc.isMasterDocument) {
      return {
        label: 'Master Document',
        className: 'bg-success-subtle text-success',
      };
    }
    return {
      label: 'Upload Custom',
      className: 'bg-primary-subtle text-primary',
    };
  };

  return (
    <>
      <Row className='mb-3'>
        <Col sm={12}>
          <h5 className='mb-1'>Document Attachment</h5>
          <p className='text-muted mb-0 fs-12'>
            Review supporting documents submitted for this offering.
          </p>
        </Col>
      </Row>

      <hr className='my-3 border-secondary opacity-25' />

      <Row>
        <Col sm={12} className='mb-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h6 className='fw-semibold text-dark mb-0'>
              Procurement Documents
            </h6>
          </div>

          {isLoading ? (
            <div className='text-center py-5'>
              <Spinner size='sm' color='primary' />
              <span className='text-muted ms-2 fs-13'>
                Loading documents...
              </span>
            </div>
          ) : procurementDocuments.length === 0 ? (
            <div
              className='border rounded-3 p-4 d-flex align-items-center justify-content-center text-muted bg-light bg-opacity-50'
              style={{ minHeight: '120px' }}
            >
              <i className='ri-file-list-3-line fs-24 me-2' />
              No procurement documents available.
            </div>
          ) : (
            <div>
              {procurementDocuments.map(doc => {
                const badge = getBadgeMeta(doc);
                return (
                  <DocumentRow
                    key={doc.id}
                    doc={doc}
                    badgeLabel={badge.label}
                    badgeClass={badge.className}
                    onView={handleView}
                  />
                );
              })}
            </div>
          )}
        </Col>

        <Col sm={12} className='mb-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h6 className='fw-semibold text-dark mb-0'>Other Documents</h6>
          </div>

          {isLoading ? (
            <div className='text-center py-4'>
              <Spinner size='sm' color='primary' />
            </div>
          ) : otherDocuments.length === 0 ? (
            <div
              className='border rounded-3 p-4 d-flex align-items-center justify-content-center text-muted bg-light bg-opacity-50'
              style={{ minHeight: '120px' }}
            >
              <i className='ri-upload-cloud-2-line fs-24 me-2' />
              No other documents available.
            </div>
          ) : (
            <div>
              {otherDocuments.map(doc => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  badgeLabel='Upload Custom'
                  badgeClass='bg-primary-subtle text-primary'
                  onView={handleView}
                />
              ))}
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};

export default SupportingDocumentTab;
